import { getStyleFilterGroupList, StyleFilterAnalyticsResponse } from '@/service/filter';
import { MarketAudioDto } from '@/service/type';
import { FilterGroup } from '@/service/type/Filter';
import { Filter } from '@/service/type/FilterDto';
import { useRequest } from 'ahooks';
import { useEffect } from 'react';
import { create } from 'zustand';

interface StyleFilterStore {
  styleFilterList: Filter[];
  setStyleFilterList: (data: Filter[]) => void;

  groupListData: FilterGroup[];
  setGroupListData: (data: FilterGroup[]) => void;

  marketAudioListData: Record<string, MarketAudioDto[]>;
  setMarketAudioListData: (data: Record<string, MarketAudioDto[]>) => void;

  selectedFiltersState: Record<number, { value: number; id: number | undefined }>;
  updateFilterSelection: (filterId: number, selection: { value: number; id: number | undefined }) => void;

  isLoading: boolean;

  initializeTrackLists: () => Promise<void>;

  analyticsData: StyleFilterAnalyticsResponse;
  setAnalyticsData: (data: StyleFilterAnalyticsResponse) => void;
}

const STORAGE_KEY = 'marketAudioListData';

export const useStyleFilterStore = create<StyleFilterStore>((set, get) => ({
  styleFilterList: [],
  setStyleFilterList: (data) => set({ styleFilterList: data }),

  groupListData: [],
  setGroupListData: (data) => set({ groupListData: data }),

  marketAudioListData: (() => {
    try {
      const storedData = sessionStorage.getItem(STORAGE_KEY);
      return storedData ? JSON.parse(storedData) : {};
    } catch {
      return {};
    }
  })(),
  setMarketAudioListData: (data) => {
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    } catch (error) {
      console.error('Failed to save marketAudioListData to sessionStorage:', error);
    }
    set({ marketAudioListData: data });
  },

  selectedFiltersState: {},
  updateFilterSelection: (filterId, selection) =>
    set((state) => ({
      selectedFiltersState: {
        ...state.selectedFiltersState,
        [filterId]: selection,
      },
    })),

  isLoading: false,

  initializeTrackLists: async () => {
    const storedData = sessionStorage.getItem(STORAGE_KEY);
    if (storedData) {
      return;
    }

    set({ isLoading: true });

    try {
      const urls = [
        'https://app.jenmusic.ai/cdn/sf_example/IMO-WHYDTM/list.json',
        'https://app.jenmusic.ai/cdn/sf_example/IMO-HEADLOCK/list.json',
        'https://app.jenmusic.ai/cdn/sf_example/IMO-LNE/list.json',
        'https://storage.googleapis.com/jen-prod-cdn-bucket/cdn/sf_example/IMO-JFN/list.json',
        'https://storage.googleapis.com/jen-prod-cdn-bucket/cdn/sf_example/IMO-GNG/list.json',
      ];

      const [whydtmData, headlockData, lneData, jfnData, gngData] = await Promise.all(
        urls.map(async (url) => {
          const response = await fetch(url);
          if (!response.ok) {
            throw new Error(`Failed to fetch ${url}`);
          }
          const jsonData = await response.json();
          return jsonData as MarketAudioDto[];
        }),
      );

      const newData = {
        'IMO-WHYDTM': whydtmData,
        'IMO-HEADLOCK': headlockData,
        'IMO-LNE': lneData,
        'IMO-JFN': jfnData,
        'IMO-GNG': gngData,
        'IMO-ALL': [...whydtmData, ...headlockData, ...lneData, ...jfnData, ...gngData].sort(() => Math.random() - 0.5),
      };

      set({
        isLoading: false,
        marketAudioListData: newData,
      });

      try {
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify(newData));
      } catch (error) {
        console.error('Failed to save marketAudioListData to sessionStorage:', error);
      }
    } catch (error) {
      console.error('Failed to fetch track lists:', error);
      set({ isLoading: false });
    }
  },

  analyticsData: {},
  setAnalyticsData: (data) => set({ analyticsData: data }),
}));

export const useTrackLists = () => {
  const store = useStyleFilterStore();
  const { data: getStyleFilterGroupData } = useRequest(getStyleFilterGroupList);

  useEffect(() => {
    store.initializeTrackLists();
    if (getStyleFilterGroupData?.data) {
      store.setGroupListData(getStyleFilterGroupData.data);
    }
  }, [getStyleFilterGroupData?.data]);

  return {
    isLoading: store.isLoading,
    groupListData: store.groupListData,
    marketAudioListData: store.marketAudioListData,
  };
};
